import React from 'react'
import styled from 'styled-components'
import { CardRow } from '../../../../../generic/ui/CardRow'
import { Mono } from '../../../../../generic/ui/Mono'
import BarcodeIcon from '../../../../../generic/icons/IconLib/BarcodeIcon'
import PackageBoxIcon from '../../../../../generic/icons/IconLib/PackageBoxIcon'
import ScaleIcon from '../../../../../generic/icons/IconLib/ScaleIcon'
import { IEntriesItem } from '../../../../../modules/API/Miun/models/sortingList'
import { EXIST_PATH } from '../../../../../modules/API/settings'

interface Props {
    item: IEntriesItem
}

const DivideItemCard: React.FC<Props> = ({ item }) => {
    const {
        EntryNum,
        DetailNum,
        ProductDesc,
        VarietyDesc,
        SizeDesc,
        Pack_Name,
        AmountPack,
        Gross,
        GeneralProductID,
    } = item

    const product = `${ProductDesc?.trim() ?? ''} ${VarietyDesc?.trim() ?? ''} ${SizeDesc?.trim() ?? ''}`.trim()
    const productImg = GeneralProductID
        ? `${EXIST_PATH}/images/products/${GeneralProductID}.png`
        : '/emptyBox.png'

    return (
        <Wrapper>
            <CardRow
                $columns='2.75rem minmax(0, 1fr)'
                $mobileColumns='2.5rem minmax(0, 1fr)'
                $isLast
                $hover={false}
            >
                <CardRow.Cell $align='center'>
                    <ProductImg
                        src={productImg}
                        alt={product}
                        loading='lazy'
                        decoding='async'
                        onError={(e) => {
                            ;(e.currentTarget as HTMLImageElement).src = '/emptyBox.png'
                        }}
                    />
                </CardRow.Cell>

                <CardRow.Cell>
                    <CardRow.Stack $gap='0.25rem'>
                        <TopLine>
                            <IdGroup>
                                <IconSlot>
                                    <BarcodeIcon color='currentColor' size={14} />
                                </IconSlot>
                                <Id>{`${EntryNum}-${DetailNum}`}</Id>
                            </IdGroup>
                            <WeightLine>
                                <IconSlot $muted>
                                    <ScaleIcon />
                                </IconSlot>
                                <Mono>{Gross ? Gross.toFixed(0) : '0'}</Mono>
                            </WeightLine>
                        </TopLine>
                        {Pack_Name && (
                            <MetaLine>
                                <MetaIcon>
                                    <PackageBoxIcon />
                                </MetaIcon>
                                <MetaText>
                                    {Pack_Name.trim()}
                                    {' · '}
                                    <Mono>{AmountPack}</Mono>
                                </MetaText>
                            </MetaLine>
                        )}
                    </CardRow.Stack>
                </CardRow.Cell>
            </CardRow>
        </Wrapper>
    )
}

export default DivideItemCard

const Wrapper = styled.div`
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    background: ${({ theme }) => theme.colors.surface};
    overflow: hidden;
`

const ProductImg = styled.img`
    width: 2.5rem;
    height: 2.5rem;
    object-fit: contain;
    border-radius: 0.5rem;
    user-select: none;
    pointer-events: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 2.25rem;
        height: 2.25rem;
    }
`

const TopLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
`

const IdGroup = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
`

const IconSlot = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : theme.colors.ink2)};
    flex-shrink: 0;

    svg {
        width: 1rem;
        height: 1rem;
    }
`

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`

const MetaLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
`

const MetaIcon = styled.span`
    color: ${({ theme }) => theme.colors.ink3};
    display: inline-flex;
    align-items: center;

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`

const MetaText = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`

const WeightLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 600;
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`
